import i18n from './i18n';

/* ---------------------------------------------------------------------------
   WhatsApp Embedded Signup (Meta). החלון של Meta מחזיר שני דברים בנפרד:
     • code חד-פעמי — דרך callback של FB.login;
     • phone_number_id + waba_id — דרך postMessage מ-facebook.com.
   מחכים לשניהם, ורק אז שולחים לשרת (שמחליף את ה-code בטוקן).
   --------------------------------------------------------------------------- */

interface FacebookSdk {
  init(opts: { appId: string; autoLogAppEvents: boolean; xfbml: boolean; version: string }): void;
  login(cb: (res: { authResponse?: { code?: string } | null }) => void, opts: Record<string, unknown>): void;
}

declare global {
  interface Window {
    FB?: FacebookSdk;
    fbAsyncInit?: () => void;
  }
}

const GRAPH_VERSION = 'v23.0';
let loading: Promise<FacebookSdk> | null = null;

function loadSdk(appId: string): Promise<FacebookSdk> {
  if (window.FB) return Promise.resolve(window.FB);
  loading ??= new Promise<FacebookSdk>((resolve, reject) => {
    window.fbAsyncInit = () => {
      window.FB!.init({ appId, autoLogAppEvents: true, xfbml: false, version: GRAPH_VERSION });
      resolve(window.FB!);
    };
    const script = document.createElement('script');
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.onerror = () => {
      loading = null;
      reject(new Error(i18n.t('common:whatsappSignup.sdkFailed')));
    };
    document.body.appendChild(script);
  });
  return loading;
}

export class SignupCancelled extends Error {}

export async function launchWhatsAppSignup(app: { appId: string; configId: string }): Promise<{
  code: string;
  phoneNumberId: string;
  wabaId: string;
}> {
  const FB = await loadSdk(app.appId);
  return new Promise((resolve, reject) => {
    let code: string | null = null;
    let ids: { phoneNumberId: string; wabaId: string } | null = null;
    const done = () => {
      if (code && ids) {
        window.removeEventListener('message', onMessage);
        resolve({ code, ...ids });
      }
    };
    const onMessage = (event: MessageEvent) => {
      // רק מ-Meta. כל postMessage אחר מתעלמים ממנו.
      if (!/^https:\/\/([a-z0-9-]+\.)*facebook\.com$/.test(event.origin)) return;
      let data: { type?: string; event?: string; data?: { phone_number_id?: string; waba_id?: string } };
      try {
        data = (typeof event.data === 'string' ? JSON.parse(event.data) : event.data) as typeof data;
      } catch {
        return;
      }
      if (data?.type !== 'WA_EMBEDDED_SIGNUP') return;
      if (data.event === 'CANCEL') {
        window.removeEventListener('message', onMessage);
        reject(new SignupCancelled(i18n.t('common:whatsappSignup.cancelled')));
        return;
      }
      if (data.event?.startsWith('FINISH') && data.data?.phone_number_id && data.data.waba_id) {
        ids = { phoneNumberId: data.data.phone_number_id, wabaId: data.data.waba_id };
        done();
      }
    };
    window.addEventListener('message', onMessage);
    FB.login(
      (res) => {
        if (!res.authResponse?.code) {
          window.removeEventListener('message', onMessage);
          reject(new SignupCancelled(i18n.t('common:whatsappSignup.cancelled')));
          return;
        }
        code = res.authResponse.code;
        done();
        // המזהים מגיעים ב-postMessage, לפעמים אחרי ה-callback. בלי מספר
        // (למשל רק חשבון עסקי נוצר) — לא נחכה לנצח.
        setTimeout(() => {
          if (ids) return;
          window.removeEventListener('message', onMessage);
          reject(new Error(i18n.t('common:whatsappSignup.noPhone')));
        }, 15_000);
      },
      {
        config_id: app.configId,
        response_type: 'code',
        override_default_response_type: true,
        extras: { setup: {}, featureType: '', sessionInfoVersion: '3' },
      },
    );
  });
}
